import {
  Box,
  Button,
  Drawer,
  Link,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import {
  Outlet,
  Link as RouterLink,
  useLocation,
  useNavigate,
  useOutletContext,
  useParams,
} from "react-router-dom";
import VideoContentIconSVG from "assets/images/VideoContentIcon.svg";
import React, { useEffect, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import CommentIcon from "@mui/icons-material/Comment";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { type } from "os";
import { NewTranslation } from "components/NewContent";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { getContent } from "services/firebase";
import { LanguageCode, languageMap } from "types";
import { Timestamp } from "firebase/firestore";

export interface TranslationData {
  title: string;
  merge_file: string;
  target_transcript: string;
  target_transcript_timestamp: string;
  description: string;
}

export interface Content {
  title: string;
  description: string;
  date: Timestamp;
  originalLanguage: LanguageCode;
  source_media: string;
  source_transcript: string;
  source_transcript_timestamp: string;
  status: string;
  type: "video" | "podcast";
  translatedLanguages: Partial<Record<LanguageCode, TranslationData>>;
}

// maybe use redux for this instead?
type ContextType = { content: Content };

export const useContentData = () => {
  return useOutletContext<ContextType>();
};

const ContentLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id, language } = useParams();
  const [content, setContent] = useState<Content>({
    title: "",
    description: "",
    date: Timestamp.now(),
    originalLanguage: "en",
    source_media: "",
    source_transcript: "",
    source_transcript_timestamp: "",
    status: "",
    type: "video",
    translatedLanguages: {},
  });
  const [openTranslationDrawer, setOpenTranslationDrawer] = useState(false);
  const [selectedLanguages, setSelectedLanguages] = useState<Set<string>>(
    new Set<string>()
  );

  useEffect(() => {
    if (!id) return;
    getContent(id)
      .then((data) => {
        console.log("content data", data);
        if (!data) return;
        setContent({
          title: data.title,
          description: data.description,
          date: data.date,
          originalLanguage: data.originalLanguage,
          source_media: `${id}_${data.source_filename}`,
          source_transcript: data.source_transcript,
          source_transcript_timestamp: data.source_transcript_timestamp,
          status: data.status,
          type: data.type,
          translatedLanguages: {
            ...(Object.entries(data.translatedLanguages) as [LanguageCode, TranslationData][]).reduce((acc, [key, value]) => {
              acc[key] = {
                ...value,
                title: data.translatedTitle,
                description: data.translatedDescription
              };
              return acc;
            }, {} as Partial<Record<LanguageCode, TranslationData>>),
            [data.originalLanguage]: {
              title: data.title,
              merge_file: `${id}_${data.source_filename}`,
              target_transcript: data.source_transcript,
              target_transcript_timestamp: data.source_transcript_timestamp,
              description: data.description,
            },
          },
        });
      })
      .catch((error) => {
        console.log(error);
      });
  }, []);

  const handleNewTranslation = () => {
    // TODO: Create new translation on this content
    console.log("new translation for languages:", selectedLanguages);
    setSelectedLanguages(new Set<string>());
    setOpenTranslationDrawer(false);
  };

  const handleSelectLanguage = (language: string) => {
    if (location.pathname.includes("/transcript")) {
      navigate(`/content/${id}/${language}/transcript`);
    }
    navigate(`/content/${id}/${language}`);
  };

  return (
    <Stack sx={{ width: "100%" }}>
      <Stack sx={{ color: "white", background: "black", px: 7, pb: 10 }}>
        <Link
          component={RouterLink}
          to="/content"
          sx={{ textDecoration: "none", color: "white" }}
        >
          <Stack direction={"row"} sx={{ py: 2 }}>
            <ArrowBackIcon sx={{ mr: 2 }} />
            <Typography sx={{ fontWeight: 700, textTransform: "uppercase" }}>
              back to content manager
            </Typography>
          </Stack>
        </Link>
        <Stack direction="row">
          <Box component={"img"} src={VideoContentIconSVG} sx={{ pr: 4 }} />
          <Stack>
            <Typography variant="h4">{content && content.title}</Typography>
            <Typography variant="body2" sx={{ textTransform: "capitalize" }}>
              {content &&
                `${content.date.toDate().toLocaleDateString("en-US")} | ${
                  content.type
                } Content`}
            </Typography>
          </Stack>
          {/* TODO: upload progress */}
        </Stack>
      </Stack>

      <Stack
        direction="row"
        sx={{
          justifyContent: "space-between",
          borderTopRightRadius: 36,
          borderTopLeftRadius: 36,
          pl: 7.5,
          pr: 4,
          py: 1,
          mt: -5,
          bgcolor: "white",
          alignItems: "center",
        }}
      >
        <Typography variant="h6">Content Languages:</Typography>
        <Stack direction={"row"} spacing={4}>
          <Tabs
            value={language}
            onChange={(e: React.SyntheticEvent, languageOption) =>
              handleSelectLanguage(languageOption)
            }
          >
            {Object.keys(content.translatedLanguages).map((languageOption) => (
              <Tab
                key={languageOption}
                value={languageOption}
                onClick={() => handleSelectLanguage(languageOption)}
                label={
                  <Typography
                    sx={{
                      fontWeight: language === languageOption ? 700 : 400,
                      textTransform: "capitalize",
                    }}
                  >
                    {languageMap[languageOption as LanguageCode]}
                  </Typography>
                }
                sx={{
                  mx: 2,
                }}
              />
            ))}
          </Tabs>
          <Button
            onClick={() => setOpenTranslationDrawer(true)}
            sx={{ color: "secondary.dark" }}
            value={"newTranslation"}
          >
            <AddIcon sx={{ fontSize: 18, fontWeight: 700 }} />
            New Translation
          </Button>
        </Stack>
        {content && (
          <Drawer
            anchor="right"
            open={openTranslationDrawer}
            onClose={() => setOpenTranslationDrawer(false)}
          >
            <NewTranslation
              title={content.title}
              type={content.type}
              selectedLanguages={selectedLanguages}
              setSelectedLanguages={setSelectedLanguages}
              handleNewTranslation={handleNewTranslation}
            />
          </Drawer>
        )}
      </Stack>

      <Outlet context={{ content }} />
    </Stack>
  );
};

export default ContentLayout;
